import * as React from 'react';
import { Accordion as AccordionPrimitive } from '@base-ui/react/accordion';

import { ChevronDownIcon } from '@/ui/icons';
import { cn } from '@/ui/lib/utils';

// 企業一覧のサイドバー（カテゴリ絞り込み）のデザインに合わせた開閉リスト。
// 開いた状態は「同じ枠の中に、区切り線付きの行が全幅で並ぶ」表現
//（行の構成・スタイルはページ側の責務。story の Expanded を参照）。
// 文字色はデザインの navy（#242a5e）でなく DS の foreground に寄せている。
// 現行ページは各リストが独立して開閉するため、multiple の既定を true に
// 反転している（Base UI の既定は false）。

// Base UI は className に状態関数を渡せるため、cn() で潰さずに合成する
function withBase<State>(
  base: string,
  className?: string | ((state: State) => string | undefined),
) {
  return typeof className === 'function'
    ? (state: State) => cn(base, className(state))
    : cn(base, className);
}

const Accordion = React.forwardRef<
  HTMLDivElement,
  AccordionPrimitive.Root.Props
>(({ className, multiple = true, ...props }, ref) => (
  <AccordionPrimitive.Root
    ref={ref}
    data-slot="accordion"
    multiple={multiple}
    className={withBase('tw:flex tw:w-full tw:flex-col tw:gap-3', className)}
    {...props}
  />
));
Accordion.displayName = 'Accordion';

const AccordionItem = React.forwardRef<
  HTMLDivElement,
  AccordionPrimitive.Item.Props
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    data-slot="accordion-item"
    className={withBase(
      'tw:box-border tw:rounded-lg tw:border tw:border-border tw:bg-card tw:text-card-foreground',
      className,
    )}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

// Base UI の Header は既定で h3 を描画する。ページの見出し構造は h1 の直下が
// h3 になるとは限らないため、レベルを指定できるようにする（既定は Base UI と同じ h3）。
type AccordionTriggerProps = AccordionPrimitive.Trigger.Props & {
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
};

const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  AccordionTriggerProps
>(({ className, children, headingLevel: Heading = 'h3', ...props }, ref) => (
  <AccordionPrimitive.Header className="tw:m-0 tw:flex" render={<Heading />}>
    <AccordionPrimitive.Trigger
      ref={ref}
      data-slot="accordion-trigger"
      className={withBase(
        'tw:group/accordion-trigger tw:box-border tw:flex tw:h-14 tw:flex-1 tw:cursor-pointer tw:items-center tw:justify-between tw:gap-2 tw:rounded-lg tw:border-0 tw:bg-transparent tw:px-4 tw:text-left tw:text-base tw:font-bold tw:text-inherit tw:outline-none tw:focus-visible:ring-3 tw:focus-visible:ring-ring tw:focus-visible:ring-offset-2 tw:focus-visible:ring-offset-background',
        className,
      )}
      {...props}
    >
      {children}
      {/* 開閉はシェブロンの回転で表現する */}
      <ChevronDownIcon className="tw:size-6 tw:shrink-0 tw:transition-transform tw:group-aria-expanded/accordion-trigger:rotate-180" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = 'AccordionTrigger';

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  AccordionPrimitive.Panel.Props
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Panel
    ref={ref}
    data-slot="accordion-content"
    className={withBase(
      'tw:overflow-hidden tw:text-base tw:data-open:animate-accordion-down tw:data-closed:animate-accordion-up',
      className,
    )}
    {...props}
  >
    {/* 区切り線を枠いっぱいに引けるよう、パネル自体は余白を持たない。
        余白が必要な場合は行側に持たせる（Panel に padding を足すと
        高さアニメーションが崩れるため不可）。
        overflow-hidden のため、行のフォーカスリングは外側に描くと切り取られる。
        行側は ring-inset で内側に描くこと（story の FocusedRow を参照） */}
    {children}
  </AccordionPrimitive.Panel>
));
AccordionContent.displayName = 'AccordionContent';

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
